
// src/pages/Page2.js

import React, { useCallback } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { jsPDF } from 'jspdf';
import Logo from './logo.png'; // Ensure the correct path to your logo

function Page2({ formData }) {
  const {
    annualFinancialGuarantee,
    hasCollateral,
    collateralAmount,
    stopLossSpend,
    actuarialSpend,
    py2025Programs = [],
    msspTracks = [],
    companyName,
    email,
  } = formData;

  const isNonZero = (val) => {
    if (!val) return false;
    const num = parseFloat(val);
    return !isNaN(num) && num !== 0;
  };

  const showCollateral = hasCollateral === 'Yes' && isNonZero(collateralAmount);
  const showMSSPTracks = py2025Programs.includes('MSSP');

  const fgVal = isNonZero(annualFinancialGuarantee) ? parseFloat(annualFinancialGuarantee) : 0;
  const slVal = isNonZero(stopLossSpend) ? parseFloat(stopLossSpend) : 0;
  const actVal = isNonZero(actuarialSpend) ? parseFloat(actuarialSpend) : 0;
  const collVal = showCollateral ? parseFloat(collateralAmount) : 0;

  const fgSparx = fgVal ? fgVal * 0.75 : 0;   // 25% cheaper
  const slSparx = slVal ? slVal * 0.95 : 0;   // 5% cheaper
  const actSparx = actVal ? actVal * 0.75 : 0; // 25% cheaper

  const generatePdfAndSend = useCallback(async () => {
    // 1) Build PDF with jsPDF
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const currentDate = new Date().toLocaleDateString();

    // Header with logo
    doc.setFillColor(101, 52, 223);
    doc.rect(0, 0, pageWidth, 80, 'F');
    doc.addImage(Logo, 'PNG', pageWidth - 200, 10, 180, 60);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(255, 255, 255);
    doc.text('Sparx | Equitable Health Infrastructure', 20, 30);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text('Prepared by: Aakarsh Rai, Head of Partnerships', 20, 50);
    doc.text(`Date: ${currentDate}`, 20, 65);

    let currentY = 100;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);

    // SUMMARY PARAGRAPH
    const summaryText =
      'Summary: These are only the tangible financial savings associated with vendor consolidation via Sparx RiskOS. '
      + 'Our software, team of actuaries, and financial products have improved patient outcomes, decreased MLR and utilization, '
      + 'streamlined network construction, offered support on contract negotiation, and most of all have ensured our CFO '
      + 'and Chief Actuary counterparts sleep well at night!';
    const summaryLines = doc.splitTextToSize(summaryText, pageWidth - 40);
    doc.text(summaryLines, 20, currentY);
    currentY += summaryLines.length * 12 + 20;

    // BOX: ROI
    const hasAnySpend = fgVal > 0 || slVal > 0 || actVal > 0;
    if (hasAnySpend) {
      doc.rect(20, currentY, 260, 130); // Box for ROI
      let boxTextX = 30;
      let boxTextY = currentY + 15;

      doc.setFont('helvetica', 'bold');
      doc.text('Return on Investment by Partnering with Sparx:', boxTextX, boxTextY);
      boxTextY += 20;

      // Financial Guarantee
      if (fgVal > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text('• Savings on Financial Guarantee: ', boxTextX, boxTextY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(101, 52, 223); // Purple
        doc.text(`$${(fgVal - fgSparx).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, boxTextX + doc.getTextWidth('• Savings on Financial Guarantee:'), boxTextY);
        boxTextY += 30;
      }

      // Stop Loss
      if (slVal > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text('• Savings on Stop Loss Policy: ', boxTextX, boxTextY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(101, 52, 223);
        doc.text(`$${(slVal - slSparx).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, boxTextX + doc.getTextWidth('• Savings on Stop Loss Policy:'), boxTextY);
        boxTextY += 30;
      }

      // Actuarial
      if (actVal > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0, 0, 0);
        doc.text('• Savings on Actuarial Services: ', boxTextX, boxTextY);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(101, 52, 223);
        doc.text(`$${(actVal - actSparx).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, boxTextX + doc.getTextWidth('• Savings on Actuarial Services:'), boxTextY);
        boxTextY += 30;
      }

      // Capital Enablement Potential ROI
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text('• Capital Enablement Potential ROI: ', boxTextX, boxTextY);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(101, 52, 223);
      doc.text(`$${((collVal + (actVal - actSparx) + (slVal - slSparx) + (fgVal - fgSparx)) * 0.15).toLocaleString('en-US', {
        minimumFractionDigits: 0, maximumFractionDigits: 0, useGrouping: true
      })}`, boxTextX + doc.getTextWidth('• Capital Enablement Potential ROI:'), boxTextY);

      // ROI Bullet Points on the Right
      const roiX = 300;
      let roiY = currentY;

      roiY += 15;
      if (fgVal > 0) {
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        const bp = '• Sparx Bond Price: ';
        doc.text(bp, roiX, roiY);
        const bpWidth = doc.getTextWidth(bp);
        doc.setTextColor(101, 52, 223); // Purple
        doc.text(`$${fgSparx.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} `, roiX + bpWidth, roiY);
        roiY += 15;
        doc.setTextColor(255, 0, 0);
        doc.setFont('helvetica', 'normal');
        const incFG = `  Original Bond Price: $${fgVal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
        doc.text(incFG, roiX, roiY);
        const incFGWidth = doc.getTextWidth(incFG);
        doc.line(roiX, roiY - 3, roiX + incFGWidth, roiY - 3);
        roiY += 15;
      }

      if (collVal > 0) {
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        const coll = '• Sparx Collateral: ';
        doc.text(coll, roiX, roiY);
        const collWidth = doc.getTextWidth(coll);
        doc.setTextColor(101, 52, 223);
        doc.text(`$0.00`, roiX + collWidth, roiY);
        roiY += 15;
        doc.setTextColor(255, 0, 0);
        doc.setFont('helvetica', 'normal');
        const incColl = `  Original Collateral: $${collVal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
        doc.text(incColl, roiX, roiY);
        const incCollWidth = doc.getTextWidth(incColl);
        doc.line(roiX, roiY - 3, roiX + incCollWidth, roiY - 3);
        roiY += 15;
      }

      if (slVal > 0) {
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        const sl = '• Sparx Stop Loss Price: ';
        doc.text(sl, roiX, roiY);
        const slWidth = doc.getTextWidth(sl);
        doc.setTextColor(101, 52, 223);
        doc.text(`$${slSparx.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, roiX + slWidth, roiY);
        roiY += 15;
        doc.setTextColor(255, 0, 0);
        doc.setFont('helvetica', 'normal');
        const incSL = `  Original Stop Loss Price: $${slVal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
        doc.text(incSL, roiX, roiY);
        const incSLWidth = doc.getTextWidth(incSL);
        doc.line(roiX, roiY - 3, roiX + incSLWidth, roiY - 3);
        roiY += 15;
      }

      if (actVal > 0) {
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        const act = '• Sparx Actuarial Services Price: ';
        doc.text(act, roiX, roiY);
        const actWidth = doc.getTextWidth(act);
        doc.setTextColor(101, 52, 223);
        doc.text(`$${actSparx.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, roiX + actWidth, roiY);
        roiY += 15;
        doc.setTextColor(255, 0, 0);
        doc.setFont('helvetica', 'normal');
        const incAct = `  Original Actuarial Services Price: $${actVal.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
        doc.text(incAct, roiX, roiY);
        const incActWidth = doc.getTextWidth(incAct);
        doc.line(roiX, roiY - 3, roiX + incActWidth, roiY - 3);
        roiY += 15;
      }

      const boxBottom = currentY + 120;
      currentY = Math.max(boxBottom, roiY) + 30;
    }

    // Display PY2025 Programs
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    if (py2025Programs.length > 0) {
      doc.text(`PY2025 Programs: ${py2025Programs.join(', ')}`, 20, currentY);
      currentY += 15;
    }
    if (showMSSPTracks) {
      doc.text(`MSSP Tracks: ${msspTracks.join(', ')}`, 20, currentY);
      currentY += 15;
    }
    currentY += 20;

    // CONSOLIDATED PARTNER SAVINGS + ASSUMPTIONS
    if (hasAnySpend) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text('Consolidated Partner Savings:', 20, currentY);
      currentY += 20;

      const totalIncumbent = fgVal + slVal + actVal + collVal;
      const totalSparx = fgSparx + slSparx + actSparx;
      const totalSavings = totalIncumbent - totalSparx;
      if (totalSavings > 0) {
        doc.setFont('helvetica', 'normal');
        doc.text(`Original Total: $${totalIncumbent.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 30, currentY);
        currentY += 15;
        doc.text(`Sparx Total: $${totalSparx.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 30, currentY);
        currentY += 15;
        doc.text(`Total Potential Savings: $${totalSavings.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`, 30, currentY);
        currentY += 20;
      }

      // Assumptions
      doc.setFont('helvetica', 'bold');
      doc.text('Assumptions:', 20, currentY);
      currentY += 15;
      doc.setFont('helvetica', 'normal');
      doc.text('• For Financial Guarantee expenditure, we assume savings of up to 25%', 30, currentY);
      currentY += 15;
      doc.text('• For Stop Loss Policies, we assume up to 5% savings', 30, currentY);
      currentY += 15;
      doc.text('• For Actuarial Services, we assume up to 25% savings', 30, currentY);
      currentY += 15;
      const capenabText = '• For Capital Enablement Return on Investment, we assume a 15% return when effectively investing said funds elsewhere within the organization';
      const capenabLines = doc.splitTextToSize(capenabText, pageWidth - 40);
      doc.text(capenabLines, 30, currentY);
      currentY += 30;
    }

    // DISCLAIMER
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    const disclaimerText =
      'Disclaimer: This analysis is provided solely for educational purposes. The results presented herein are ' +
      'estimates derived from the information provided and may not accurately reflect actual outcomes. These ' +
      'calculations do not constitute a promise or guarantee regarding eligibility or the terms of any specific service.\n\n' +
      'Confidentiality: The contents of this document are strictly confidential.';
    const disclaimerLines = doc.splitTextToSize(disclaimerText, pageWidth - 40);
    doc.text(disclaimerLines, 20, currentY);

    // Convert PDF to Base64
    const pdfBase64 = doc.output('datauristring'); // e.g., "data:application/pdf;base64,JVBERi0..."

    // Send the PDF and form data to Netlify Function
    try {
      const response = await fetch('/.netlify/functions/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName,
          email,
          Name: formData.Name,
          designation: formData.designation,
          annualFinancialGuarantee,
          hasCollateral,
          collateralAmount,
          stopLossSpend,
          actuarialSpend,
          py2025Programs,
          msspTracks,
          pdfBase64, // Attach the entire PDF in base64
        }),
      });

      if (response.ok) {
        console.log('PDF emailed and data sent to Zapier successfully');
      } else {
        console.error('Failed to email PDF or send data to Zapier');
      }
    } catch (error) {
      console.error('Error sending PDF and data:', error);
    }
  }, [
    formData.designation,
    formData.Name,
    annualFinancialGuarantee,
    hasCollateral,
    collateralAmount,
    stopLossSpend,
    actuarialSpend,
    py2025Programs,
    msspTracks,
    companyName,
    email,
    fgVal,
    slVal,
    actVal,
    collVal,
    fgSparx,
    slSparx,
    actSparx,
    showMSSPTracks,
  ]);

  // Generate PDF and send when Page2 loads
  React.useEffect(() => {
    generatePdfAndSend();
  }, [generatePdfAndSend]);

  return (
    <Box sx={{ margin: 2 }}>
      <Paper sx={{ padding: 2, maxWidth: 600, margin: '0 auto' }}>
        {/* The short formal message */}
        <Typography variant="h5" gutterBottom>
          Thank you for your time!
        </Typography>
        <Typography variant="body1">
          An email containing your PDF has been sent to your provided email address.
          Please let us know if you have any further questions.
        </Typography>
      </Paper>
    </Box>
  );
}

export default Page2;